import { useEffect, useRef, type KeyboardEvent } from "react";
import "./searchInput.scss";
import { useToast } from "../Toast/Toast";
import { _ } from "i18n";

interface SearchInputProps {
	clear: () => void;
	onSubmit: () => void;
	onChange: (value: string) => void;
	value: string;
	placeholder?: string;
}

/**
 * Strip characters that break search backends.
 * Keep letters, digits, spaces, hyphens/dashes, underscore, dot, and apostrophes (O'Connor).
 * Also collapse Chrome's doubled apostrophe (`''` → `'`).
 */
const sanitizeSearchInputValue = (rawValue: string): string =>
	rawValue
		.replace(/'{2,}/g, "'")
		.replace(/’{2,}/g, "’")
		.replace(/[^a-zA-ZÀ-ž0-9\s\-–_.'’]+/g, "")
		.replace(/^\s+/, "");

/**
 * Uncontrolled while the user types on purpose.
 *
 * Chromium bug: assigning `input.value` inside an `oninput` handler (including React
 * controlled `value={...}` sync) re-fires input with the last character doubled.
 * Firefox re-fires too but with the correct value — so this only shows up in Chrome.
 *
 * @see https://github.com/microsoft/fluentui/issues/19054
 */
export default function SearchInput({ clear, onChange, onSubmit, value, placeholder }: SearchInputProps) {
	const { addErrorToast } = useToast();
	const inputRef = useRef<HTMLInputElement>(null);
	const lastEmittedValueRef = useRef(value);

	// External resets only (Clear filters / parent set to ""). Never write during typing.
	useEffect(() => {
		const input = inputRef.current;
		if (input === null) {
			return;
		}
		if (value === "" && input.value !== "") {
			input.value = "";
			lastEmittedValueRef.current = "";
		}
	}, [value]);

	const emitIfChanged = (nextValue: string) => {
		if (nextValue === lastEmittedValueRef.current) {
			return;
		}
		lastEmittedValueRef.current = nextValue;
		onChange(nextValue);
	};

	const handleNativeInput = () => {
		const input = inputRef.current;
		if (input === null) {
			return;
		}
		const collapsed = input.value.replace(/'{2,}/g, "'").replace(/’{2,}/g, "’");
		if (collapsed !== input.value) {
			// Correct after the input event — writing during oninput re-triggers Chrome's bug.
			window.setTimeout(() => {
				const currentInput = inputRef.current;
				if (currentInput === null) {
					return;
				}
				currentInput.value = collapsed;
				emitIfChanged(collapsed);
			}, 0);
			return;
		}
		emitIfChanged(input.value);
	};

	const handleClear = () => {
		const input = inputRef.current;
		if (input !== null) {
			input.value = "";
		}
		lastEmittedValueRef.current = "";
		onChange("");
		clear();
		input?.focus();
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (event.key !== "Enter") {
			return;
		}

		event.preventDefault();

		const input = inputRef.current;
		const rawValue = input?.value ?? "";
		const sanitized = sanitizeSearchInputValue(rawValue);

		// Sanitize only on submit (not while typing). Safe: not inside an input event.
		if (input !== null && input.value !== sanitized) {
			input.value = sanitized;
		}
		lastEmittedValueRef.current = sanitized;
		onChange(sanitized);

		if (sanitized === "") {
			clear();
			return;
		}

		if (sanitized.length >= 2) {
			onSubmit();
			return;
		}

		addErrorToast(_("club_pro.club_amateur.search.placeholder"));
	};

	return (
		<div className="searchInput-container">
			<input
				ref={inputRef}
				className="searchField"
				placeholder={placeholder}
				type="text"
				defaultValue={value}
				autoComplete="off"
				autoCorrect="off"
				autoCapitalize="off"
				spellCheck={false}
				onInput={handleNativeInput}
				onKeyDown={handleKeyDown}
			/>
			<div className={`searchIcon ${value ? "inactive" : ""}`}>
				<span className="asideListSearch__submit-icon icon icon-search icon-new icon-grey icon-12" />
			</div>
			<div className={`clearButton ${value ? "" : "inactive"}`} onClick={handleClear}>
				<span className="asideListSearch__clear-icon icon icon-light-cross icon icon-grey icon-new icon-12" />
			</div>
		</div>
	);
}
